/**
 * ConcediiTab - Componenta pentru tab-ul Concedii
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Palmtree, Download, Edit, ArrowRight, RefreshCw, AlertCircle, CheckCircle2, Search, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Componenta ConcediiTab
 */
const ConcediiTab = memo(function ConcediiTab({
  concediiYear,
  setConcediiYear,
  concediiFilter,
  setConcediiFilter,
  concediiSubTab,
  setConcediiSubTab,
  concediiData,
  concediiLoading,
  loading,
  exportConcediiPDF,
  exportConcediiExcel,
  fetchConcediiDetail,
  saveSoldConcediu,
  hasPermission,
  refreshConcediiData,
  auth
}) {
  // Local state pentru dialoguri
  const [concediiDetailDialog, setConcediiDetailDialog] = useState({
    open: false,
    employeeId: null,
    employeeName: '',
    month: null,
    tip: 'CO',
    intervals: [],
    totalDays: 0
  });
  
  const [soldEditDialog, setSoldEditDialog] = useState({
    open: false,
    employeeId: null,
    employeeName: '',
    soldValue: 0
  });
  
  // State pentru căutare angajat
  const [searchQuery, setSearchQuery] = useState('');
  
  // State pentru transfer sold
  const [transferDialog, setTransferDialog] = useState({
    open: false,
    fromYear: null,
    toYear: null,
    loading: false,
    result: null
  });
  
  const [transferStatus, setTransferStatus] = useState(null);
  
  // Check transfer status when year changes
  useEffect(() => {
    const checkTransferStatus = async () => {
      if (!auth?.token) return;
      try {
        const response = await axios.get(
          `${API}/concedii/transfer-status/${concediiYear}`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setTransferStatus(response.data);
      } catch (err) {
        console.error('Error checking transfer status:', err);
        setTransferStatus(null);
      }
    };
    checkTransferStatus();
  }, [concediiYear, auth?.token]);
  
  // Handler pentru transfer sold
  const handleTransferSold = async () => {
    setTransferDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await axios.post(
        `${API}/concedii/transfer-sold/${transferDialog.fromYear}`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      setTransferDialog(prev => ({ 
        ...prev, 
        loading: false, 
        result: response.data 
      }));
      // Refresh data after transfer
      if (refreshConcediiData) {
        refreshConcediiData();
      }
      // Update transfer status
      setTransferStatus({
        transferred: true,
        transferred_from: transferDialog.fromYear,
        transferred_at: new Date().toISOString(),
        transferred_by: auth.user?.username
      });
    } catch (err) {
      console.error('Error transferring sold:', err);
      setTransferDialog(prev => ({ 
        ...prev, 
        loading: false, 
        result: { success: false, message: err.response?.data?.detail || 'Eroare la transfer' }
      }));
    }
  };

  // Handler pentru click pe celulă lunară
  const handleMonthCellClick = async (empId, empName, month, tip) => {
    const result = await fetchConcediiDetail(empId, empName, month, tip);
    if (result) {
      setConcediiDetailDialog({
        open: true,
        employeeId: empId,
        employeeName: empName,
        month: month,
        tip: tip,
        intervals: result.intervals || [],
        totalDays: result.totalDays || 0
      });
    }
  };

  // Handler pentru salvare sold
  const handleSaveSold = async () => {
    await saveSoldConcediu(soldEditDialog.employeeId, soldEditDialog.soldValue);
    setSoldEditDialog(prev => ({ ...prev, open: false }));
  };

  const MONTHS_SHORT = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const MONTHS_FULL = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];

  // Filtrare angajați după căutare
  const filteredEmployees = concediiData.employees?.filter(emp => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const fullName = `${emp.nume || ''} ${emp.prenume || ''}`.toLowerCase();
    const username = (emp.username || '').toLowerCase();
    return fullName.includes(query) || username.includes(query);
  }) || [];

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              {/* Title and Year Selector */}
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Palmtree className="w-5 h-5 text-green-600" />
                    Situație Concedii {concediiYear}
                  </CardTitle>
                  <CardDescription>Evidența anuală: CO, CM, CFP, CNC (Naștere Copil), CD (Deces)</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {/* Year Navigation */}
                  <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setConcediiYear(concediiYear - 1)}
                      className="h-8 w-8 p-0"
                    >
                      ◀
                    </Button>
                    <span className="font-semibold text-lg min-w-[60px] text-center">{concediiYear}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setConcediiYear(concediiYear + 1)}
                      className="h-8 w-8 p-0"
                    >
                      ▶
                    </Button>
                  </div>
                  {/* Employee Filter */}
                  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                    <Button
                      variant={concediiFilter === 'activi' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setConcediiFilter('activi')}
                      className="h-8 px-3"
                    >
                      Activi
                    </Button>
                    <Button
                      variant={concediiFilter === 'fosti' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setConcediiFilter('fosti')}
                      className="h-8 px-3"
                    >
                      Foști
                    </Button>
                    <Button
                      variant={concediiFilter === 'toti' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setConcediiFilter('toti')}
                      className="h-8 px-3"
                    >
                      Toți
                    </Button>
                  </div>
                  {/* Transfer Sold Button - Only show for CO sub-tab */}
                  {concediiSubTab === 'CO' && hasPermission('concedii.editare_sold') && (
                    <Button
                      variant="outline"
                      className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => setTransferDialog({
                        open: true,
                        fromYear: concediiYear - 1,
                        toYear: concediiYear,
                        loading: false,
                        result: null
                      })}
                    >
                      <ArrowRight className="w-4 h-4" />
                      Transfer Sold {concediiYear - 1} → {concediiYear}
                    </Button>
                  )}
                  {/* Export Buttons */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2" disabled={loading}>
                        <Download className="w-4 h-4" />
                        Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={exportConcediiPDF}>
                        📄 Export PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportConcediiExcel}>
                        📊 Export Excel
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Transfer Status Info */}
              {concediiSubTab === 'CO' && transferStatus?.transferred && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-green-700">
                    Soldul din {transferStatus.transferred_from} a fost transferat în {concediiYear} 
                    {transferStatus.transferred_by && ` de ${transferStatus.transferred_by}`}
                    {transferStatus.transferred_at && ` la ${new Date(transferStatus.transferred_at).toLocaleDateString('ro-RO')}`}
                  </span>
                </div>
              )}
              
              {/* Sub-tabs: CO, CM, CFP */}
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setConcediiSubTab('CO')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    concediiSubTab === 'CO' 
                      ? 'bg-green-600 text-white shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  🌴 CO - Concediu de Odihnă
                </button>
                <button
                  onClick={() => setConcediiSubTab('CM')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    concediiSubTab === 'CM' 
                      ? 'bg-orange-500 text-white shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  🏥 CM - Concediu Medical
                </button>
                <button
                  onClick={() => setConcediiSubTab('CFP')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    concediiSubTab === 'CFP' 
                      ? 'bg-purple-600 text-white shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  📋 CFP - Fără Plată
                </button>
                <button
                  onClick={() => setConcediiSubTab('CNC')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    concediiSubTab === 'CNC' 
                      ? 'bg-pink-600 text-white shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  👶 CNC - Naștere Copil
                </button>
                <button
                  onClick={() => setConcediiSubTab('CD')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    concediiSubTab === 'CD' 
                      ? 'bg-gray-600 text-white shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  🖤 CD - Deces
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4 flex items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Caută angajat..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-8"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <span className="text-sm text-slate-500">
                  {filteredEmployees.length} rezultat{filteredEmployees.length !== 1 ? 'e' : ''}
                </span>
              )}
            </div>
            
            {concediiLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-slate-500">Se încarcă datele...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-2 py-2 text-left border font-semibold text-slate-700 w-16">Nr.Ctr</th>
                      <th className="px-2 py-2 text-left border font-semibold text-slate-700 min-w-[150px]">Angajat</th>
                      {concediiSubTab === 'CO' && (
                        <th className="px-2 py-2 text-center border font-semibold text-slate-700 w-16 bg-amber-50">
                          Sold<br/>{concediiYear - 1}
                        </th>
                      )}
                      {MONTHS_SHORT.map((month, idx) => (
                        <th key={month} className="px-1 py-2 text-center border font-semibold text-slate-600 w-10">
                          {month}
                        </th>
                      ))}
                      {concediiSubTab === 'CO' ? (
                        <>
                          <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-blue-50">
                            Luate<br/>{concediiYear}
                          </th>
                          <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-green-50">
                            {concediiFilter === 'fosti' ? 'Disp.' : 'Disp.'}<br/>{concediiFilter === 'fosti' ? 'demisie' : 'azi'}
                          </th>
                          {concediiFilter !== 'fosti' && (
                            <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-green-50">
                              Disp.<br/>sf.an
                            </th>
                          )}
                        </>
                      ) : (
                        <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-blue-50">
                          Total<br/>{concediiYear}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees && filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, idx) => {
                        const monthData = concediiSubTab === 'CO' 
                          ? emp.co_by_month 
                          : concediiSubTab === 'CM' 
                            ? emp.cm_by_month 
                            : concediiSubTab === 'CNC'
                              ? emp.cnc_by_month
                              : concediiSubTab === 'CD'
                                ? emp.cd_by_month
                                : emp.cfp_by_month;
                        
                        const total = concediiSubTab === 'CO' 
                          ? emp.total_co 
                          : concediiSubTab === 'CM' 
                            ? emp.total_cm 
                            : concediiSubTab === 'CNC'
                              ? emp.total_cnc
                              : concediiSubTab === 'CD'
                                ? emp.total_cd
                                : emp.total_cfp;
                        
                        const displayName = `${emp.nume || ''} ${emp.prenume || ''}`.trim() || emp.username;
                        
                        return (
                          <tr key={emp.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-2 py-1.5 border text-center text-slate-600">
                              {emp.numar_contract || '-'}
                            </td>
                            <td className="px-2 py-1.5 border font-medium">
                              {displayName}
                              {emp.pontaj_pana_la && (
                                <span className="ml-1 text-xs text-red-500" title={`Demisionat: ${emp.pontaj_pana_la.split('T')[0]}`}>
                                  (D)
                                </span>
                              )}
                            </td>
                            {concediiSubTab === 'CO' && (
                              <td 
                                className="px-2 py-1.5 border text-center bg-amber-50 cursor-pointer hover:bg-amber-100"
                                onClick={() => hasPermission('concedii.editare_sold') && setSoldEditDialog({
                                  open: true,
                                  employeeId: emp.id,
                                  employeeName: displayName,
                                  soldValue: emp.sold_an_anterior || 0
                                })}
                                title={hasPermission('concedii.editare_sold') ? "Click pentru a edita soldul" : ""}
                              >
                                <span className="font-medium">{emp.sold_an_anterior || 0}</span>
                                {hasPermission('concedii.editare_sold') && (
                                  <Edit className="w-3 h-3 inline ml-1 text-slate-400" />
                                )}
                              </td>
                            )}
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => {
                              const val = monthData?.[month] || monthData?.[String(month)] || 0;
                              const hasValue = val > 0;
                              
                              return (
                                <td 
                                  key={month}
                                  className={`px-1 py-1.5 border text-center ${
                                    hasValue 
                                      ? 'cursor-pointer hover:bg-blue-50' 
                                      : ''
                                  }`}
                                  onClick={() => hasValue && handleMonthCellClick(emp.id, displayName, month, concediiSubTab)}
                                  title={hasValue ? 'Click pentru detalii' : ''}
                                >
                                  {hasValue ? (
                                    <span className={`font-medium ${
                                      concediiSubTab === 'CO' ? 'text-green-600' :
                                      concediiSubTab === 'CM' ? 'text-orange-600' :
                                      'text-purple-600'
                                    }`}>
                                      {val}
                                    </span>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              );
                            })}
                            {concediiSubTab === 'CO' ? (
                              <>
                                <td className="px-2 py-1.5 border text-center bg-blue-50 font-semibold">
                                  {total}
                                </td>
                                <td className={`px-2 py-1.5 border text-center font-bold ${
                                  emp.disp_azi < 0 ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-600'
                                }`}>
                                  {emp.disp_azi}
                                </td>
                                {concediiFilter !== 'fosti' && (
                                  <td className={`px-2 py-1.5 border text-center font-bold ${
                                    emp.disp_sf_an < 0 ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-600'
                                  }`}>
                                    {emp.disp_sf_an}
                                  </td>
                                )}
                              </>
                            ) : (
                              <td className="px-2 py-1.5 border text-center bg-blue-50 font-semibold">
                                {total}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={concediiSubTab === 'CO' ? (concediiFilter === 'fosti' ? 16 : 17) : 15} className="px-4 py-8 text-center text-slate-500">
                          Nu există date de concediu pentru acest an
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-600 border-t pt-3">
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 bg-green-600 rounded"></span>
                <span>CO - Concediu de Odihnă</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 bg-orange-500 rounded"></span>
                <span>CM - Concediu Medical</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 bg-purple-600 rounded"></span>
                <span>CFP - Concediu Fără Plată</span>
              </div>
              {concediiSubTab === 'CO' && (
                <>
                  <span className="text-slate-400">|</span>
                  <span><strong>Disp. azi</strong> = Sold + (luni × zile_contract/12) - Luate</span>
                  <span><strong>Disp. sf.an</strong> = Sold + (luni_lucrate × zile_contract/12) - Luate</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Dialog pentru detalii concediu */}
      <Dialog open={concediiDetailDialog.open} onOpenChange={(open) => !open && setConcediiDetailDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Detalii {concediiDetailDialog.tip === 'CO' ? 'Concediu de Odihnă' : 
                       concediiDetailDialog.tip === 'CM' ? 'Concediu Medical' : 'Concediu Fără Plată'}
            </DialogTitle>
            <DialogDescription>
              {concediiDetailDialog.employeeName} - {MONTHS_FULL[concediiDetailDialog.month]} {concediiYear}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {concediiDetailDialog.intervals && concediiDetailDialog.intervals.length > 0 ? (
              <>
                <div className="space-y-2">
                  {concediiDetailDialog.intervals.map((interval, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span className="font-medium">{interval.formatted}</span>
                      <span className={`px-2 py-1 rounded text-white text-sm ${
                        concediiDetailDialog.tip === 'CO' ? 'bg-green-600' :
                        concediiDetailDialog.tip === 'CM' ? 'bg-orange-500' : 'bg-purple-600'
                      }`}>
                        {interval.days} {interval.days === 1 ? 'zi' : 'zile'}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">Total:</span>
                  <span className="font-bold">{concediiDetailDialog.totalDays} zile</span>
                </div>
              </>
            ) : (
              <p className="text-center text-slate-500 py-4">Nu există înregistrări</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog pentru editare sold CO */}
      <Dialog open={soldEditDialog.open} onOpenChange={(open) => !open && setSoldEditDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Editare Sold CO</DialogTitle>
            <DialogDescription>
              Sold concediu an anterior pentru {soldEditDialog.employeeName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Zile CO rămase din {concediiYear - 1}</Label>
              <Input
                type="number"
                min="0"
                value={soldEditDialog.soldValue}
                onChange={(e) => setSoldEditDialog(prev => ({ ...prev, soldValue: parseInt(e.target.value) || 0 }))}
              />
              <p className="text-xs text-slate-500">
                Acestea sunt zilele de concediu neefectuate în anul {concediiYear - 1} care se reportează în {concediiYear}.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoldEditDialog(prev => ({ ...prev, open: false }))}>
              Anulează
            </Button>
            <Button onClick={handleSaveSold}>
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog pentru transfer sold */}
      <Dialog open={transferDialog.open} onOpenChange={(open) => !open && setTransferDialog(prev => ({ ...prev, open: false, result: null }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-amber-600" />
              Transfer Sold Concedii
            </DialogTitle>
            <DialogDescription>
              Transferă automat soldurile de concediu din {transferDialog.fromYear} în {transferDialog.toYear}
            </DialogDescription>
          </DialogHeader>
          
          {!transferDialog.result ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Ce face această acțiune?</p>
                    <ul className="mt-2 space-y-1 text-amber-700">
                      <li>• Calculează zilele CO rămase la sfârșitul lui {transferDialog.fromYear} pentru fiecare angajat</li>
                      <li>• Setează aceste zile ca <strong>sold an anterior</strong> pentru {transferDialog.toYear}</li>
                      <li>• Soldurile negative devin 0</li>
                      <li>• Puteți modifica manual fiecare sold după transfer</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Formula de calcul:</p>
                <p className="text-slate-600 font-mono text-xs">
                  Sold {transferDialog.toYear} = Sold {transferDialog.fromYear - 1} + Zile CO anuale - Zile CO efectuate în {transferDialog.fromYear}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {transferDialog.result.success ? (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 font-medium">
                      <CheckCircle2 className="w-5 h-5" />
                      {transferDialog.result.message}
                    </div>
                  </div>
                  
                  {transferDialog.result.details && transferDialog.result.details.length > 0 && (
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Angajat</th>
                            <th className="text-right p-2">Sold {transferDialog.fromYear}</th>
                            <th className="text-right p-2">Zile anuale</th>
                            <th className="text-right p-2">CO efectuat</th>
                            <th className="text-right p-2 font-bold">Sold nou</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferDialog.result.details.map((d, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-2">{d.employee_name}</td>
                              <td className="text-right p-2">{d.sold_from_year}</td>
                              <td className="text-right p-2">{d.zile_co_anuale}</td>
                              <td className="text-right p-2">{d.total_co_taken}</td>
                              <td className="text-right p-2 font-bold text-green-600">{d.new_sold}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium">
                    <AlertCircle className="w-5 h-5" />
                    {transferDialog.result.message || 'Eroare la transfer'}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {!transferDialog.result ? (
              <>
                <Button variant="outline" onClick={() => setTransferDialog(prev => ({ ...prev, open: false }))}>
                  Anulează
                </Button>
                <Button 
                  onClick={handleTransferSold}
                  disabled={transferDialog.loading}
                  className="gap-2 bg-amber-600 hover:bg-amber-700"
                >
                  {transferDialog.loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Se transferă...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      Transferă Soldurile
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => setTransferDialog(prev => ({ ...prev, open: false, result: null }))}>
                Închide
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default ConcediiTab;
